import React, { useCallback, useEffect, useRef, useState } from "react";
import { Collapse, CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { ApiService, CompanyLogoService } from "../../../../api";
import { CustomButton, createNotification, icon, IconComponent, CustomIconButton } from "../../../../common/components";
import { isSegFeatureEnabled, SegFlags, translate, translateDate } from "../../../../common/providers";
import { formatFileSize, validateLogoFile } from "./utils/logo-utils";
import LogoEmptyState from "./components/LogoEmptyState";
import LogoHistoryTable from "./components/LogoHistoryTable";
import LogoDeleteDialog from "./components/LogoDeleteDialog";
import styles from "./AdminCompanyLogo.module.css";
import { isNonEmptyArray } from "../../../../common/utils";
import { useApi } from "../../../../common/hooks";

const DIALOG_MODE = { ACTIVE: "active", ALL: "all" };

function ActiveLogoSection({ activeLogo, logoObjectUrl, uploading, uploadError, onFileChange, onDeleteActive }) {
	return (
		<>
			<div className={styles.previewBox}>
				{logoObjectUrl ? (
					<img alt={activeLogo.filename} className={styles.previewImage} src={logoObjectUrl} />
				) : (
					<CircularProgress size={24} />
				)}
			</div>
			<p className={styles.metadata}>
				<span className={styles.metadata__item}>{activeLogo.filename}</span>
				<span className={styles.metadata__dot}>&bull;</span>
				<span className={styles.metadata__item}>{formatFileSize(activeLogo.fileSize)}</span>
				<span className={styles.metadata__dot}>&bull;</span>
				<span className={styles.metadata__item}>{activeLogo.contentType}</span>
				{activeLogo.width && activeLogo.height && (
					<>
						<span className={styles.metadata__dot}>&bull;</span>
						<span className={styles.metadata__item}>
							{activeLogo.width}&times;{activeLogo.height}&nbsp;px
						</span>
					</>
				)}
			</p>
			<p className={styles.uploadedAt}>
				{translate("admin.company-logo.uploaded-at", {
					date: translateDate(activeLogo.uploadedAt),
				})}
			</p>
			{uploadError && <p className={styles.errorText}>{uploadError}</p>}
			<div className={styles.actions}>
				<CustomButton
					className={styles.emptyState__uploadLabel}
					color="secondary"
					component="label"
					variant="outlined"
				>
					<input
						accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
						className={styles.hiddenInput}
						disabled={uploading}
						id="logo-upload-replace"
						type="file"
						onChange={onFileChange}
					/>
					{uploading ? (
						<CircularProgress color="inherit" size={16} />
					) : (
						<IconComponent color="var(--grey-400)" icon={icon.faFileUpload} />
					)}
					{translate("admin.company-logo.btn.upload")}
				</CustomButton>
				<CustomButton color="error" startIcon={icon.faTrash} variant="outlined" onClick={onDeleteActive}>
					{translate("admin.company-logo.btn.delete-active")}
				</CustomButton>
			</div>
		</>
	);
}

function HistoryContent({ historyLoading, history, onDeleteAll }) {
	if (historyLoading) {
		return (
			<div className={styles.loaderWrapper}>
				<CircularProgress size={24} />
			</div>
		);
	}
	if (isNonEmptyArray(history)) {
		return (
			<>
				<LogoHistoryTable history={history} />
				<div className={styles.historyFooter}>
					<CustomButton
						color="error"
						size="sm"
						startIcon={icon.faTrash}
						variant="outlined"
						onClick={onDeleteAll}
					>
						{translate("admin.company-logo.btn.delete-all")}
					</CustomButton>
				</div>
			</>
		);
	}
	return <p className={styles.noHistory}>{translate("admin.company-logo.history.empty")}</p>;
}

export default function AdminCompanyLogo() {
	const companyId = useSelector(({ context }) => context.company?.id);
	const [activeLogo, setActiveLogo] = useState(null);
	const [logoObjectUrl, setLogoObjectUrl] = useState(null);
	const [history, setHistory] = useState([]);
	const [loading, setLoading] = useState(true);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [uploadError, setUploadError] = useState(null);
	const [historyOpen, setHistoryOpen] = useState(false);
	const [deleteDialog, setDeleteDialog] = useState({ open: false, mode: null });
	const cancelTokenRef = useRef(null);
	const objectUrlsRef = useRef([]);
	const { call: getLogoImage } = useApi(CompanyLogoService.getLogoImage);
	const { call: getActiveLogo } = useApi(CompanyLogoService.getActiveLogo);
	const { call: getLogoHistory } = useApi(CompanyLogoService.getLogoHistory);
	const { call: uploadLogo } = useApi(CompanyLogoService.uploadLogo);
	const { call: deleteAllLogos } = useApi(CompanyLogoService.deleteAllLogos);
	const { call: deleteActiveLogo } = useApi(CompanyLogoService.deleteActiveLogo);

	const revokeObjectUrl = (url) => {
		if (url && url.startsWith("blob:")) {
			URL.revokeObjectURL(url);
		}
	};

	useEffect(() => {
		cancelTokenRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenRef.current);
			if (objectUrlsRef?.current) {
				// eslint-disable-next-line react-hooks/exhaustive-deps
				objectUrlsRef.current.forEach(revokeObjectUrl);
			}
		};
	}, []);

	const setLogoWithBlob = useCallback(
		(logoDto) => {
			setActiveLogo(logoDto);
			if (!logoDto?.url) {
				setLogoObjectUrl(null);
				return;
			}
			getLogoImage(logoDto.url)
				.then((blob) => {
					const objectUrl = URL.createObjectURL(blob);
					objectUrlsRef.current.push(objectUrl);
					setLogoObjectUrl(objectUrl);
				})
				.catch((err) => {
					if (!ApiService.isRequestCancellation(err)) {
						console.error(err);
						setLogoObjectUrl(null);
					}
				});
		},
		[getLogoImage]
	);

	const loadActiveLogo = useCallback(() => {
		if (!companyId) {
			return;
		}
		setLoading(true);
		getActiveLogo({ companyId })
			.then(setLogoWithBlob)
			.catch(() => {
				setActiveLogo(null);
				setLogoObjectUrl(null);
			})
			.finally(() => setLoading(false));
	}, [companyId, getActiveLogo, setLogoWithBlob]);

	const loadHistory = useCallback(() => {
		if (!companyId || isSegFeatureEnabled(SegFlags.DISABLED)) {
			return;
		}
		setHistoryLoading(true);
		getLogoHistory({ companyId })
			.then((data) => setHistory(isNonEmptyArray(data) ? data : []))
			.catch(console.error)
			.finally(() => setHistoryLoading(false));
	}, [companyId, getLogoHistory]);

	useEffect(() => {
		loadActiveLogo();
	}, [loadActiveLogo]);

	const handleFileChange = (e) => {
		const file = e.target.files?.[0];
		if (!file) {
			return;
		}
		e.target.value = "";
		const { valid, error } = validateLogoFile(file);
		if (!valid) {
			setUploadError(
				error === "size-exceeded"
					? translate("admin.company-logo.error.size-exceeded")
					: translate("admin.company-logo.error.invalid-type")
			);
			return;
		}
		setUploadError(null);
		setUploading(true);
		uploadLogo({ companyId, file })
			.then(() => {
				loadActiveLogo();
				createNotification({
					type: "success",
					message: translate("admin.company-logo.upload.success"),
				});
				if (historyOpen) {
					loadHistory();
				}
			})
			.catch(console.error)
			.finally(() => setUploading(false));
	};

	const handleConfirmDelete = () => {
		const { mode } = deleteDialog;
		setDeleteDialog({ open: false, mode: null });
		if (mode === DIALOG_MODE.ALL) {
			deleteAllLogos({ companyId })
				.then(() => {
					setActiveLogo(null);
					setLogoObjectUrl(null);
					setHistory([]);
					createNotification({
						type: "success",
						message: translate("admin.company-logo.delete-all.success"),
					});
				})
				.catch(console.error);
		} else {
			deleteActiveLogo({ companyId })
				.then(() => {
					createNotification({
						type: "success",
						message: translate("admin.company-logo.delete.success"),
					});
					loadActiveLogo();
					if (historyOpen) {
						loadHistory();
					}
				})
				.catch(console.error);
		}
	};

	const handleToggleHistory = () => {
		if (!historyOpen) {
			loadHistory();
		}
		setHistoryOpen((prev) => !prev);
	};

	const renderActiveSection = () => {
		if (loading) {
			return (
				<div className={styles.loaderWrapper}>
					<CircularProgress size={32} />
				</div>
			);
		}
		if (activeLogo) {
			return (
				<ActiveLogoSection
					activeLogo={activeLogo}
					logoObjectUrl={logoObjectUrl}
					uploadError={uploadError}
					uploading={uploading}
					onDeleteActive={() => setDeleteDialog({ open: true, mode: DIALOG_MODE.ACTIVE })}
					onFileChange={handleFileChange}
				/>
			);
		}
		return (
			<>
				{uploadError && <p className={styles.errorText}>{uploadError}</p>}
				<LogoEmptyState onUpload={handleFileChange} />
			</>
		);
	};

	return (
		<div className={styles.container}>
			<div className={styles.section}>{renderActiveSection()}</div>
			{isSegFeatureEnabled(SegFlags.disabled) && (
				<div className={styles.historySection}>
					<div className={styles.historyHeader}>
						<div className={styles.historyBlock}>
							{translate("admin.company-logo.history.title")}
							<CustomIconButton
								icon={historyOpen ? icon.faChevronUp : icon.faChevronDown}
								iconColor="'var(--color-white)"
								size="sm"
								variant="text"
								onClick={handleToggleHistory}
							/>
						</div>
					</div>
					<Collapse in={historyOpen}>
						<HistoryContent
							history={history}
							historyLoading={historyLoading}
							onDeleteAll={() => setDeleteDialog({ open: true, mode: DIALOG_MODE.ALL })}
						/>
					</Collapse>
				</div>
			)}
			<LogoDeleteDialog
				mode={deleteDialog.mode}
				open={deleteDialog.open}
				onClose={() => setDeleteDialog({ open: false, mode: null })}
				onConfirm={handleConfirmDelete}
			/>
		</div>
	);
}
